import { Injectable } from '@angular/core'
import { catchError, map, Observable, of, tap } from 'rxjs'
import { WordPairDicService } from 'src/app/service/data/word-pair-dic.service'
import { DictionaryStateService } from 'src/app/service/state/dictionary-state.service'
import { HomeStateService } from 'src/app/service/state/home-state.service'
import { LibraryStateService } from 'src/app/service/state/library-state.service'
import { HomeElemType } from 'src/app/shared/models/main/home-elem'
import { CoreCommand } from '../base/core-command'
import { CommandResultService } from '../decorators/command-result.decorator'

export interface CategoryData {
  id: number
  label: string
}

export interface TagData {
  id: number
  label: string
}

export interface DictionaryData {
  id: number
  label: string
}
export interface WordData {
  language: string
  article?: string
  word: string
  pos?: string
  definition?: string
  example?: string
  category?: CategoryData
  tags?: TagData[]
}

export interface CreateWordPairDicArgs {
  word1: WordData
  word2?: WordData
  privacy: string
  dictionaries?: DictionaryData[]
  dictionary_title?: string
  dictionary_description?: string
}

@Injectable({
  providedIn: 'root',
})
export class CreateWordPairDicCommand extends CoreCommand<CreateWordPairDicArgs, any> {
  readonly id = 'create-word-pair-dic'

  constructor(
    private commandResultService: CommandResultService,
    private wordPairDicService: WordPairDicService,
    private libraryState: LibraryStateService,
    private dictionaryState: DictionaryStateService,
    private homeState: HomeStateService,
  ) {
    super()
  }

  override checkArguments(args?: CreateWordPairDicArgs): boolean {
    if (!args) return false

    if (args.dictionary_title && args.dictionary_description) return true

    // Check word1 required fields
    if (args.word1 && (!args.word1.language || !args.word1.word)) return false

    // Check word2 required fields
    if (args.word2 && (!args.word2.language || !args.word2.word)) return false

    // Check privacy
    if (!args.privacy) return false

    return true
  }

  protected doExecute(args?: CreateWordPairDicArgs): Observable<boolean> {
    return this.wordPairDicService.create(args!).pipe(
      tap(result => this.applyStateUpdates(result)),
      tap(result => {
        this.emitResult({ success: result.success, message: result.message, data: result.data })
        this.commandResultService.handleResult(this)
      }),
      map(result => result.success),
      catchError(error => {
        this.emitResult({
          success: false,
          message: error instanceof Error ? error.message : 'Failed to create word/pair',
        })
        this.commandResultService.handleError(error)
        return of(false)
      }),
    )
  }

  private applyStateUpdates(result: any): void {
    // Update library state
    if (result.data.word) {
      this.libraryState.addWord(result.data)

      result.data.type = HomeElemType.Word
      this.homeState.addWord(result.data)

      // Update dictionary state if the word belongs to the current dictionary
      this.dictionaryState.addWord(result.data)
    }
    if (result.data.word_1) {
      this.libraryState.addWord(result.data.word_1)

      result.data.word_1.type = HomeElemType.Word
      this.homeState.addWord(result.data.word_1)

      // Update dictionary state if the word belongs to the current dictionary
      this.dictionaryState.addWord(result.data.word_1)
    }
    if (result.data.word_2) {
      this.libraryState.addWord(result.data.word_2)

      result.data.word_2.type = HomeElemType.Word
      this.homeState.addWord(result.data.word_2)

      // Update dictionary state if the word belongs to the current dictionary
      this.dictionaryState.addWord(result.data.word_2)
    }
    if (result.data.word_1 && result.data.word_2) {
      this.libraryState.addPair(result.data)

      result.data.type = HomeElemType.Pair
      this.homeState.addPair(result.data)

      // Update dictionary state if the pair belongs to the current dictionary
      this.dictionaryState.addPair(result.data)
    }
    if (result.data.title) {
      this.libraryState.addDictionary(result.data)

      result.data.type = HomeElemType.Dictionary
      this.homeState.addDictionary(result.data)
    }
  }
}
